import { useState } from 'preact/hooks';
import { useAuth } from '../lib/auth';
import { route } from 'preact-router';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function BillingPage() {
  const { subscription, workspace, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only owner can access billing - redirect others
  if (user && user.role !== 'owner') {
    return (
      <div class="bento-page-container">
        <div class="bento-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Access Denied</h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
            Only the workspace owner can view and manage billing.
          </p>
          <a href="/" class="btn btn-primary">Return to Dashboard</a>
        </div>
      </div>
    );
  }

  const isOwner = user?.role === 'owner';
  const isActive = subscription?.status === 'active';
  const isTrialing = subscription?.status === 'trialing';

  async function openCustomerPortal() {
    setLoading(true);
    setError(null);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_URL}/v3/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpgrade() {
    setLoading(true);
    setError(null);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_URL}/v3/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({ plan: 'pro' })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to start checkout');
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  function getDaysRemaining(dateStr) {
    if (!dateStr) return 0;
    const endDate = new Date(dateStr);
    const now = new Date();
    return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
  }

  const planDetails = {
    trial: { name: 'Free Trial', price: '€0', features: ['Up to 5 users', 'Basic features', '14-day trial'] },
    free: { name: 'Free', price: '€0/month', features: ['Up to 5 users', 'Unlimited leave requests', 'Slack integration'] },
    pro: { name: 'Pro', price: '€39/month', features: ['Up to 50 users', 'Planner view', 'Custom leave types', 'Approval workflows', 'Priority support'] },
    business: { name: 'Business', price: '€99/month', features: ['Unlimited users', 'Multi-team support', 'Advanced analytics', 'API access', 'Dedicated support'] }
  };

  const currentPlan = planDetails[subscription?.plan] || planDetails.trial;

  return (
    <div class="billing-page">
      <div class="bento-page-header">
        <h1 class="bento-page-title">Billing & Subscription</h1>
        <p class="bento-page-subtitle">Manage your subscription and billing details</p>
      </div>

      {error && (
        <div class="alert alert-error">
          {error}
        </div>
      )}

      <div class="billing-grid">
        {/* Current Plan Card */}
        <div class="billing-card">
          <div class="card-header">
            <h2>Current Plan</h2>
            <span class={`status-badge status-${subscription?.status || 'none'}`}>
              {subscription?.status || 'No subscription'}
            </span>
          </div>
          <div class="card-body">
            <div class="plan-info">
              <div class="plan-name">{currentPlan.name}</div>
              <div class="plan-price">{currentPlan.price}</div>
            </div>

            {subscription?.discount_percent > 0 && (
              <div class="discount-badge">
                {subscription.discount_percent}% discount applied
              </div>
            )}

            <ul class="plan-features">
              {currentPlan.features.map(feature => (
                <li key={feature}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {isTrialing && (
              <div class="trial-info">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <div>
                  <strong>Trial ends {formatDate(subscription?.trial_ends_at)}</strong>
                  <span>{getDaysRemaining(subscription?.trial_ends_at)} days remaining</span>
                </div>
              </div>
            )}

            {isActive && subscription?.subscription_ends_at && (
              <div class="renewal-info">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                </svg>
                <div>
                  <strong>Next billing date</strong>
                  <span>{formatDate(subscription?.subscription_ends_at)}</span>
                </div>
              </div>
            )}

            <div class="card-actions">
              {(isTrialing || subscription?.plan === 'free' || subscription?.plan === 'trial') && (
                <button
                  class="btn btn-primary"
                  onClick={handleUpgrade}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Upgrade to Pro'}
                </button>
              )}
              {isActive && subscription?.plan !== 'business' && (
                <a href="/upgrade" class="btn btn-secondary">
                  View All Plans
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Billing Details Card */}
        <div class="billing-card">
          <div class="card-header">
            <h2>Billing Details</h2>
          </div>
          <div class="card-body">
            <dl class="billing-details">
              <dt>Workspace</dt>
              <dd>{workspace?.name || '-'}</dd>

              <dt>Billing Email</dt>
              <dd>{subscription?.billing_email || user?.email || '-'}</dd>

              {subscription?.stripe_customer_id && (
                <>
                  <dt>Customer ID</dt>
                  <dd class="text-muted">{subscription.stripe_customer_id}</dd>
                </>
              )}
            </dl>

            {subscription?.stripe_customer_id ? (
              <div class="portal-section">
                <p>Access the billing portal to:</p>
                <ul class="portal-features">
                  <li>View and download invoices</li>
                  <li>Update payment method</li>
                  <li>View billing history</li>
                  <li>Cancel subscription</li>
                </ul>
                <button
                  class="btn btn-primary btn-block"
                  onClick={openCustomerPortal}
                  disabled={loading}
                >
                  {loading ? 'Opening...' : 'Open Billing Portal'}
                </button>
              </div>
            ) : (
              <div class="no-billing">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                <p>No billing information yet.</p>
                <span class="text-muted">Billing details will appear here after you subscribe to a paid plan.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div class="billing-help">
        <h3>Need help with billing?</h3>
        <p>
          If you have questions about your subscription or billing, please contact us at{' '}
          <a href="mailto:helpdesk@vanilla.net.mt">helpdesk@vanilla.net.mt</a>
        </p>
      </div>

      <style>{`
        .billing-page {
          max-width: 1000px;
          margin: 0 auto;
        }

        .text-muted {
          color: var(--gray-500);
        }

        .alert {
          padding: 1rem;
          border-radius: var(--bento-card-radius);
          margin-bottom: 1.5rem;
        }

        .alert-error {
          background: #fef2f2;
          color: var(--danger);
          border: 1px solid #fecaca;
        }

        .billing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: var(--bento-gap);
          margin-bottom: var(--bento-gap);
        }

        .billing-card {
          background: var(--bento-card-bg);
          border-radius: var(--bento-card-radius);
          box-shadow: var(--bento-card-shadow);
          overflow: hidden;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--gray-200);
        }

        .card-header h2 {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--gray-900);
          margin: 0;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-active {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-trialing {
          background: #fef3c7;
          color: #d97706;
        }

        .status-expired, .status-cancelled {
          background: #fef2f2;
          color: #dc2626;
        }

        .card-body {
          padding: 1.5rem;
        }

        .plan-info {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .plan-price {
          font-size: 1.125rem;
          color: #64748b;
        }

        .discount-badge {
          background: #dbeafe;
          color: #2563eb;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          text-align: center;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }

        .plan-features {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem 0;
        }

        .plan-features li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0;
          color: #475569;
          font-size: 0.875rem;
        }

        .plan-features svg {
          width: 16px;
          height: 16px;
          color: #22c55e;
          flex-shrink: 0;
        }

        .trial-info, .renewal-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .trial-info svg, .renewal-info svg {
          width: 24px;
          height: 24px;
          color: #f59e0b;
          flex-shrink: 0;
        }

        .renewal-info svg {
          color: #3b82f6;
        }

        .trial-info strong, .renewal-info strong {
          display: block;
          font-size: 0.875rem;
          color: #1e293b;
        }

        .trial-info span, .renewal-info span {
          font-size: 0.75rem;
          color: #64748b;
        }

        .card-actions {
          display: flex;
          gap: 0.75rem;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.625rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: #f1f5f9;
          color: #475569;
        }

        .btn-secondary:hover {
          background: #e2e8f0;
        }

        .btn-block {
          width: 100%;
        }

        .btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .billing-details {
          margin: 0 0 1.5rem 0;
        }

        .billing-details dt {
          font-size: 0.75rem;
          font-weight: 500;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 0.25rem;
        }

        .billing-details dd {
          font-size: 0.9375rem;
          color: #1e293b;
          margin: 0 0 1rem 0;
        }

        .portal-section p {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.75rem;
        }

        .portal-features {
          list-style: none;
          padding: 0;
          margin: 0 0 1rem 0;
        }

        .portal-features li {
          font-size: 0.8125rem;
          color: #475569;
          padding: 0.25rem 0;
          padding-left: 1.25rem;
          position: relative;
        }

        .portal-features li::before {
          content: "•";
          position: absolute;
          left: 0;
          color: #3b82f6;
        }

        .no-billing {
          text-align: center;
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .no-billing svg {
          width: 48px;
          height: 48px;
          color: #cbd5e1;
          margin-bottom: 1rem;
        }

        .no-billing p {
          font-weight: 500;
          color: #475569;
          margin-bottom: 0.25rem;
        }

        .billing-help {
          background: var(--bento-card-bg);
          border-radius: var(--bento-card-radius);
          box-shadow: var(--bento-card-shadow);
          padding: 1.5rem;
          text-align: center;
        }

        .billing-help h3 {
          font-size: 1rem;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .billing-help p {
          font-size: 0.875rem;
          color: var(--gray-500);
          margin: 0;
        }

        .billing-help a {
          color: var(--primary);
        }

        @media (max-width: 640px) {
          .card-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}
